import React, { useMemo, useRef, useCallback, useState, forwardRef } from "react";
import AppBreadcrumb from "components/AppBreadcrumb";
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { Row, Col, Button, Typography, Divider, Tooltip, Comment, Avatar, Form } from "antd";
import { RightArrowIcon } from 'components/Icons';
import { BASE_PADDING } from "constants/Layout";
import ModalUtil from "utils/modalUtil";
import Container from "components/Container";
import FindingAPI from "restAPI/FindingAPI";
import TableContainerEvolved from "components/TableContainerEvolved";
import { CellRenderers, getFilterProps  } from "utils/tableUtils";
import { parseURLSearch, updateURLSearch  } from "utils/urlUtil";
import message from 'components/CustomMessage';
import { FINDING_STATUS_PROPOSED_DISPLAY_TEXT, FINDING_ACTIONS, FINDING_PROPOSED_STATUS, FINDING_STATUS} from 'views/Findings/Constants';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import { PermissionSetMap } from 'constants/RBACConstants';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import AuthorisedContainer from "components/AuthorisedContainer";
import _, { filter, isEmpty, map } from "lodash";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";
import { hasPermission } from 'utils/authService';
import BaseModalForm from "components/BaseModalForm";
import { FieldTypes } from "utils/formUtils";
import { DEFAULTS as UNSUPPRESS_DEFAULTS } from "components/CustomDuration";
import moment from "moment";
import FindingsReviewBulkAccept from "./FindingsReviewBulkAccept";
import FindingDetailsPopup from "./FindingDetails/FindingDetailsPopup";
import { disablePastDates } from "utils/dateUtils";
import useModalRenderer from "hooks/useModalRenderer";
import { FindingModalCommonConfig } from "./FindingDetails/common";
import { useFindingFilters } from "./FindingsListV3";
import ACSeverityTag from "ac-components/ACSeverityTag";
import { getProposedDataWithCustomFields } from "./FindingDetails/FindingProposalDetails";

const filterFields = [
  "severity","product", "subProduct", "environmentName", "proposer"
];

export const PROPOSED_ACTION_MAP = {
    PROPOSE_OPEN: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_OPEN,
    PROPOSE_CONFIRMED: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CONFIRMED,
    PROPOSE_ACCEPTRISK: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_ACCEPTRISK,
    PROPOSE_SUPPRESSED: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_SUPPRESSED,
    PROPOSE_FALSE_POSITIVE: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_FALSE_POSITIVE,
    PROPOSE_MITIGATED: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_MITIGATED,
    PROPOSE_ASSIGNEE: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_ASSIGNEE,
    PROPOSE_DUE_DATE: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_DUE_DATE,
    PROPOSE_CATEGORY: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CATEGORY,
    PROPOSE_SEVERITY: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_SEVERITY,
    PROPOSE_BASIC_CHANGE: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_BASIC_CHANGE,
    PROPOSE_TRIAGE: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_TRIAGE,
    PROPOSE_IN_PROGRESS: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_IN_PROGRESS,
    PROPOSE_CONTROLLED: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CONTROLLED,
    PROPOSE_CUSTOM_FIELDS: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CUSTOM_FIELDS,
}

export const PROPOSED_TYPE = {
  OPEN: {key: "open", permission: PermissionSetMap.MARK_REOPEN, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_OPEN, type: FINDING_PROPOSED_STATUS.PROPOSE_OPEN},
  CONFIRMED: {key: 'confirm', permission: PermissionSetMap.MARK_CONFIRM, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CONFIRMED, type: FINDING_PROPOSED_STATUS.PROPOSE_CONFIRMED},
  ACCEPTRISK: {key: 'accept-risk', permission: PermissionSetMap.MARK_ACCEPT_RISK, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_ACCEPTRISK, type: FINDING_PROPOSED_STATUS.PROPOSE_ACCEPTRISK},
  SUPPRESSED: {key: 'suppressed', permission: PermissionSetMap.MARK_SUPPRESSED, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_SUPPRESSED, type: FINDING_PROPOSED_STATUS.PROPOSE_SUPPRESSED},
  MITIGATED: {key: 'mitigation', permission: PermissionSetMap.MARK_MITIGATED, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_MITIGATED, type: FINDING_PROPOSED_STATUS.PROPOSE_MITIGATED},
  FALSEPOSITIVE: {key: 'false-positive', permission: PermissionSetMap.MARK_FALSE_POSITIVE, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_FALSE_POSITIVE, type: FINDING_PROPOSED_STATUS.FALSE_POSITIVE} ,
  PROPOSE_ASSIGNEE: {key: 'assignee', permission: PermissionSetMap.ASSIGN_OWNER, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_ASSIGNEE, type: FINDING_PROPOSED_STATUS.PROPOSE_ASSIGNEE},
  PROPOSE_DUE_DATE: {key: 'due-date', permission: PermissionSetMap.FINDINGS_CHANGE_DUE_DATE, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_DUE_DATE, type: FINDING_PROPOSED_STATUS.PROPOSE_DUE_DATE},
  PROPOSE_CATEGORY: {key: 'category', permission: PermissionSetMap.UPDATE_CATEGORY, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CATEGORY, type: FINDING_PROPOSED_STATUS.PROPOSE_CATEGORY},
  PROPOSE_SEVERITY: {key: 'severity', permission: PermissionSetMap.UPDATE_SEVERITY, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_SEVERITY, type: FINDING_PROPOSED_STATUS.PROPOSE_SEVERITY},
  PROPOSE_BASIC_CHANGE: {key: 'basic-change', permission: PermissionSetMap.FINDINGS_UPDATE_BASIC_DETAILS, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_BASIC_CHANGE, type: FINDING_PROPOSED_STATUS.PROPOSE_BASIC_CHANGE},
  TRIAGE:{ key: "triage", permission:PermissionSetMap.MARK_TRIAGE, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_TRIAGE, type: FINDING_PROPOSED_STATUS.PROPOSE_TRIAGE},
  CONTROLLED: {key: 'controlled', permission: PermissionSetMap.MARK_CONTROLLED, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CONTROLLED, type: FINDING_PROPOSED_STATUS.PROPOSE_CONTROLLED},
  IN_PROGRESS:{ key: "InProgress", permission:PermissionSetMap.MARK_IN_PROGRESS, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_IN_PROGRESS, type: FINDING_PROPOSED_STATUS.PROPOSE_IN_PROGRESS},
  PROPOSE_CUSTOM_FIELDS: {key: "custom-fields", permission: PermissionSetMap.PROPOSE, label: FINDING_STATUS_PROPOSED_DISPLAY_TEXT.PROPOSE_CUSTOM_FIELDS, type: FINDING_PROPOSED_STATUS.PROPOSE_CUSTOM_FIELDS},
}

export const PROPOSE_FILTER_OPTION_VALUES = {
  OPEN: () => `Propose ${getVocabulary("open")}`,
  CONFIRMED: () => `Propose ${getVocabulary("confirmed")}`,
  FALSEPOSITIVE: () => `Propose ${getVocabulary("falsePositive")}`,
  ACCEPTRISK: () => `Propose ${getVocabulary("acceptRisk")}`,
  MITIGATED: () => `Propose ${getVocabulary("mitigated")}`,
  SUPPRESSED: () => `Propose ${getVocabulary("suppressed")}`,
  TRIAGE: () => `Propose ${getVocabulary("triageStatus")}`,
  IN_PROGRESS: () => `Propose ${getVocabulary("inProgressStatus")}`,
  CONTROLLED: () => `Propose ${getVocabulary("controlledStatus")}`,
  PROPOSE_SEVERITY: () => `Propose ${getVocabulary("severity")}`,
  PROPOSE_CATEGORY: () => `Propose ${getVocabulary("category")}`,
  PROPOSE_DUE_DATE: () => `Propose ${getVocabulary("resolution")} Due date`,
  PROPOSE_BASIC_CHANGE: () => `Propose Basic Information`,
  PROPOSE_ASSIGNEE: () => `Propose ${getVocabulary("owner")}`,
  PROPOSE_CUSTOM_FIELDS: () => "Propose Custom Fields",
};

const { Text, Link } = Typography;

const FindingsReview = ({findingCustomFields}) => {
    const tableRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const activeUser = useActiveUserContext();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const modalRenderer = useModalRenderer();

    const getAllReviewFindings = (params) => {
      const newFilters = { ...params.filters, underReview: ['true'] };
      const newParams = { ...params, filters: newFilters };
      const { promise: promise1, cancel } = FindingAPI.getAllReviewed(newParams);
      const promise = promise1.then((response) => {
        response.content = map(response.content, (item) => ({
                    ...item,
                    proposedData: getProposedDataWithCustomFields(item.proposedData),
        }))
        return response
      })
      return { promise, cancel };
    };

    const refreshTable = useCallback(() => {
      tableRef.current.refresh()
    }, [])

    const showFindingDetail = useCallback(
        (e) => {
            const findingId = e.currentTarget.dataset.id;
            modalRenderer.show({
              content: <FindingDetailsPopup
                        findingId={findingId}
                      />,
              onClose: refreshTable,
              ...FindingModalCommonConfig,
            })
    }, [refreshTable, modalRenderer]);

    const { filterColumns, filterValues } = useFindingFilters({ fields: filterFields, proposeActionNullFilter: false, findingCustomFields });
    const getColumns = useMemo(() => {
      const filterColumnsMap = {};
      _.forEach(filterColumns, (c) => (filterColumnsMap[c.dataIndex] = c));
      delete filterColumnsMap.owner;
        return [
            {
                title: "ID",
                dataIndex: "id",
                key: "id",
                render: (text) => (
                  <Button
                    type="link"
                    size="small"
                    onClick={showFindingDetail}
                    data-id={text}
                  >
                    {text}
                  </Button>
                ),
                width: 120,
                default: true,
                showSort: true,
                showFilter: true,
                onlySearch: true
              },
              {
                title: <Vocabulary name="severity" />,
                dataIndex: "severity",
                key: "severity",
                render: CellRenderers.CUSTOMIZED_SEVERITY,
                width: 100,
                default: true,
                showSort: true,
                showFilter: true,
                sortOptions: false,
              },
              {
                title: "Summary",
                dataIndex: "title",
                key: "title",
                width: 250,
                render: CellRenderers.VALUE_OR_NA,
                optional: true,
                default: true,
                ellipsis: true,
                showFilter: true,
                onlySearch: true
              },
              {
                title: <Vocabulary name="product" />,
                dataIndex: "product",
                key: "product",
                render: CellRenderers.NAME_ID_MAP,
                width: 130,
                optional: true,
                default: false,
                //showSort: true,
                showFilter: true,
              },
              {
                title: <Vocabulary name="subProduct" />,
                dataIndex: "subProduct",
                key: "subProduct",
                render: CellRenderers.NAME_ID_MAP,
                width: 130,
                optional: true,
                default: false,
                //showSort: true,
                showFilter: true,
              },
              {
                title: "Environment",
                dataIndex: "environmentName",
                key: "environmentName",
                render: (record, original) => <EnvironmentName name = {original.environment?.name} />,
                filterLabelFormatter: value => CellRenderers.VOCABULARY_RENDERER(value),
                optional: true,
                default: false,
                showSort: true,
                showFilter: true,
                width: 120,
              },
              {
                title: "Propose Action",
                dataIndex: "proposeAction",
                key:"proposeAction",
                render: (record, original) => {
                  return _.map(original.proposedData, (data, i) => {
                    const actionType = getActionType(data)
                    return <div key={i}>
                      {actionType?.label || '-'}
                      {getDivider(i, original.proposedData)}
                    </div>
                  })
                },
                filterLabelFormatter: value => PROPOSED_TYPE[value]?.label,
                showFilter: true,
                default: true,
                width: 170
              },
              {
                title: <>Propose Change From <RightArrowIcon /> To</>,
                dataIndex: "proposeData",
                key:"proposeData",
                default: true,
                render: (record, original) => getProposedRenderField(original),
                width: 230
              },
              {
                title:  "Proposed By",
                dataIndex: "proposer",
                key:"proposer",
                default: true,
                render:  (__, original) => {
                  return _.map(original.proposedData, (data, i) => {
                    return (
                      <div key={i}>
                        {CellRenderers.VALUE_OR_NA(data.userName)}
                        {getDivider(i, original.proposedData)}
                      </div>
                    );
                  })
                },
                width: 120,
                ...getFilterProps({
                  filters: filterValues.proposer,
                  sortOptions: true,
                }),
              },
              {
                title: "Propose On",
                dataIndex: "findingProposalData",
                key:"findingProposalData",
                default: true,
                render:  (record, original) => {
                  return _.map(original.proposedData, (data, i) => {
                    return <div key={i}>
                      {CellRenderers.TIME_RELATIVE(data.proposedOn)}
                      {getDivider(i, original.proposedData)}
                    </div>
                  })
                },
                width: 100
              },
              {
                title: "Action",
                dataIndex: "action",
                key: 'action',
                width: 120,
                render: (text,record) => {
                  return _.map(record.proposedData, (data, i) => {
                    const actionType = getActionType(data);
                    const obj= {...data, id: record.id};
                    const permission = selectedRowKeys.length ? 'NO_PERMISSION' : actionType.permission;
                    const params = { record: obj, action: 'ACCEPT', callBack: refreshTable };
                    const customProps = { permission, params }
                    if (selectedRowKeys.length) {
                      customProps.noAccessText = null;
                    }
                    const isProposeCustomFields = data.action === FINDING_ACTIONS.PROPOSE_CUSTOM_FIELDS;
                    if (isProposeCustomFields && data.proposedValue) {
                      const proposedValuePayload = JSON.parse(data.proposedValue)[0];
                      delete proposedValuePayload.customFieldValue;
                      params.customFieldPayload = proposedValuePayload;
                    }
                    return (
                      <div key={i}>
                        <ActionComponent {...customProps}/>
                        {getDivider(i, record.proposedData)}
                      </div>
                   )
                  })
                },
                default: true
            }
        ].map(column => {
          const filterColumn = filterColumnsMap[column.dataIndex];
          return {
              ...column,
              ..._.omit(filterColumn, ["title", "dataIndex"]),
              onFilter: undefined
          }
      })
    }, [showFindingDetail, selectedRowKeys, refreshTable, filterColumns, filterValues])

    const onBulkProposed = useCallback(actionType => {
      const type = _.startCase(actionType.toLowerCase())
      ModalUtil.show({
        content: (
          <FindingsReviewBulkAccept selectedRowKeys={selectedRowKeys} actionTxt={type} setSelectedRowKeys={setSelectedRowKeys} />
        ),
        afterClose: refreshTable
      });
    }, [selectedRowKeys, refreshTable])

    const updateFilterWithOwnerId = useCallback(() => {
      const parsedUrl = parseURLSearch(location.search);
      parsedUrl.filters.owner = [activeUser.id];
      updateURLSearch(history, parsedUrl);
    }, [activeUser.id, history, location.search])

    const customProps = useMemo(() => {
      // if (!isAnyAdmin() && activeUser.role !== ROLE_SECURITY_ENGINEER) {
      //   return {};
      // }
      return {
        rowSelection: {
          selectedRowKeys: _.map(selectedRowKeys, "id"),
          columnWidth: 30,
          onChange: (id, data) => {
            setSelectedRowKeys(_.map(data, entry => ({
              id: entry.id,
              proposedData: entry.proposedData
            })))
          },
          getCheckboxProps: (record) => {
            const hasAllPermissions = !_.some(record.proposedData, data => {
              const actionType = getActionType(data);
              return !hasPermission(actionType.permission);
            });
            const isSelectable = getProposedRenderField(record) !== '--'; 
            return {
              disabled: !hasAllPermissions || !isSelectable, 
            }
          },
          preserveSelectedRowKeys: true,
        }
      }
    }, [selectedRowKeys, setSelectedRowKeys])

    const getTitle = useMemo(() => {
      return (
        <>
          {/* {
            (isAnyAdmin() ||  activeUser.role === ROLE_SECURITY_ENGINEER) && [ */}
            <Tooltip key={1} title={'Accept Selected'}><Button type="link" onClick={() => onBulkProposed('ACCEPT')} disabled={!selectedRowKeys.length}>Accept Selected</Button></Tooltip>,
            <Tooltip key={2} title={'Reject Selected'}><Button type="link" onClick={() => onBulkProposed('REJECT')} disabled={!selectedRowKeys.length}>Reject Selected</Button></Tooltip>
            {/* ]
          } */}
        </>
      )
    }, [onBulkProposed, selectedRowKeys])

    return (
        <div>
          <AppBreadcrumb crumbs={[{ text: "Findings", to: "/findings" }, `Findings Under Review`]} />
          <Container>
            <Row gutter={[BASE_PADDING, BASE_PADDING]}>
              <Col span={12} className="m-t-xs">
                <div className="d-flex flex-row">
                  <Text className="text-bold d-flex align-items-center">Findings Under Review</Text>
                </div>
              </Col>
              <Col span={12} className="m-t-xs">
                <div className="d-flex flex-row-reverse" >
                  {<Button type="link" onClick={history.goBack}>Back to Findings List</Button>}
                </div>
              </Col>
            </Row>
            <Divider type="horizontal" className="m-y-xs" />
            <Row gutter={[BASE_PADDING, BASE_PADDING]}>
              <Col span={12} className="m-t-xs">
                  <div>All Findings sent by the user to perform different proposal actions can be viewed here.</div>
              </Col>
            </Row>
            <TableContainerEvolved
                enableScroll
                title={getTitle}
                extras={ <Tooltip title={'Assigned to me'}><Button type="link" onClick={updateFilterWithOwnerId} >Assigned to me</Button></Tooltip>}
                tableKey="findings-review-list-table"
                api={getAllReviewFindings}
                columns={getColumns}
                tableRef={ref => tableRef.current = ref}
                columnsReorder
                {...customProps}
            />
          </Container>
          {modalRenderer.context}
        </div>
      );
}

export const getActionType = (record) => {
    const { action, proposedValue } = record;
    let actionType = action
    if (actionType === FINDING_ACTIONS.PROPOSE_ACTION) {
      actionType = proposedValue;
    }
    return PROPOSED_TYPE[actionType] || {};
}

export const getDivider = (index, arr=[]) => {
  return index !== arr.length - 1 ? <Divider className="m-y-xs" /> : null;
}

export const ActionComponent = ({permission, params, ...restProps}) => {
  return (
    <AuthorisedContainer permission={permission} {...restProps}>
      <Link onClick={() => onProposeAcceptReject(params)}>Accept</Link>
      <Divider type="vertical" />
      <Link onClick={() => onProposeAcceptReject({ ...params, action: 'REJECT'})}>Reject</Link>
    </AuthorisedContainer>
  )
}

const onProposeAcceptReject = ({ record, action, callBack, customFieldPayload }) => {
  ModalUtil.show({
    content: <AcceptProposalPopup record={record} action={action} customFieldPayload={customFieldPayload} />,
    afterClose: callBack
  })
}

export const getProposedRenderField = (record, combined = true) => {
  const { proposedData=[], status, armorcodeCategory, slaDueDate, owner, severity, product={}, subProduct={}, environment={}, envName, customFields } = record;
  if (!proposedData?.length) {
    return '--';
  }
  return _.map(proposedData, (data, i) => {
    let value = '--';
    const { action, proposedValue } = data;
      if (action === FINDING_ACTIONS.PROPOSE_ACTION) {
        const fromTxt = FINDING_STATUS_PROPOSED_DISPLAY_TEXT[status];
        const toTxt = FINDING_STATUS_PROPOSED_DISPLAY_TEXT[proposedValue];
         value =  <>{fromTxt} <RightArrowIcon /> {toTxt}</>;
      } else if (action === FINDING_ACTIONS.PROPOSE_DUE_DATE) {
        const fromDate = slaDueDate ? CellRenderers.DATE(slaDueDate) : CellRenderers.VALUE_OR_NA(slaDueDate);
        const toDate = CellRenderers.DATE(parseInt(proposedValue) * 1000); // multiple by 1000 to convert unix timestamp
          value =  <>{fromDate} <RightArrowIcon /> {toDate}</>;
      } else if (action === FINDING_ACTIONS.PROPOSE_CATEGORY) {
        value =  <>{CellRenderers.VALUE_OR_NA(armorcodeCategory)} <RightArrowIcon /> {proposedValue}</>;
      } else if (action === FINDING_ACTIONS.PROPOSE_SEVERITY) {
        value =  <span className='d-flex align-items-center'>{<ACSeverityTag value={severity} className="m-l-xs m-r-xs" />} <RightArrowIcon /> {<ACSeverityTag value={proposedValue} className="m-l-xs" />}</span>;
      }  else if (action === FINDING_ACTIONS.PROPOSE_ASSIGNEE) {
        value =  <>{CellRenderers.VALUE_OR_NA(owner?.name)} <RightArrowIcon /> {proposedValue}</>;
      } else if (action === FINDING_ACTIONS.PROPOSE_BASIC_CHANGE) {
        value =  <><b><Vocabulary name="product"/></b> {CellRenderers.VALUE_OR_NA(product.name)} <b><Vocabulary name="subProduct"/></b> {CellRenderers.VALUE_OR_NA(subProduct.name)} <b>Environment</b> {(envName || environment.name) ? <EnvironmentName name={envName || environment.name} /> : CellRenderers.VALUE_OR_NA()} <RightArrowIcon /> {proposedValue}</>;
      } else if (action === FINDING_ACTIONS.PROPOSE_CUSTOM_FIELDS){
        value = <>{getCustomFieldsFromValue(customFields, proposedValue)} <RightArrowIcon /> {proposedValue ? getCustomFieldsToText(proposedValue) : [proposedValue]}</>
      }
      return <div key={i}>
        {value}
        {getDivider(i, proposedData)}
      </div>
  })
}

const AcceptProposalPopup = forwardRef(function AcceptProposalPopup({ record, action, afterClose, customFieldPayload }, ref) {
  const [form] = Form.useForm();
  const actionTxt = _.startCase(action.toLowerCase());
  const proposedValue = record.proposedValue;
  const dueDateValue = (record.action === FINDING_ACTIONS.PROPOSE_DUE_DATE && actionTxt === 'Accept') ? moment(parseInt(record.proposedValue) * 1000) : moment();
  const fields = useMemo(() => {
    const fields = [];

    if((proposedValue === FINDING_STATUS.ACCEPT_RISK || proposedValue === FINDING_STATUS.CONTROLLED) && actionTxt === 'Accept'){
      fields.push({
        name: 'reason',
        label: 'Reason',
        type: FieldTypes.SELECT,
        required: true,
        props: {
            autoFocus: true,
            api: proposedValue === FINDING_STATUS.ACCEPT_RISK ? FindingAPI.getAcceptRiskReasons : FindingAPI.getControlledReasons
        }
      });
      fields.push({
        name: 'unsupressAfterDuration',
        label: "Re-Open",
        type: FieldTypes.CUSTOM_DURATION,
        required: true,
        props: {
          showNeverOption: proposedValue === FINDING_STATUS.ACCEPT_RISK ? false : true
        }
      })
    }
    if (record.action === FINDING_ACTIONS.PROPOSE_DUE_DATE && actionTxt === 'Accept') {
      fields.push( {
        name: 'dueDate',
        label: <><Vocabulary name="resolution" /> Due Date</>,
        type: FieldTypes.DATE,
        required:true,
        props: {
          disabledDate: disablePastDates,
         }
      });
    }
    fields.push({
      name: "note",
      type: FieldTypes.TEXT_AREA,
      required: true,
      label: "Note",
      customLengthRule: {
          max: 4000,
          message: "Maximum length is 4000 characters" 
      },
    });
    return fields;
  }, [proposedValue, actionTxt, record.action]);

  const onOk = useCallback(async (values) => {
    let unsupressAfterDuration;
    let data = {
      notes: values.note,
      reason: values.reason,
    }
    if(record.action === "PROPOSE_CUSTOM_FIELDS"){
      data = {
        ...data,
        ...customFieldPayload
      }
    }
    const formFields = form.getFieldsValue();
    if (formFields.unsupressAfterDuration?.neverOrAfter !== "Never" && values.unsupressAfterDuration) {
      unsupressAfterDuration = moment().add(+(values.unsupressAfterDuration.number), _.lowerCase(values.unsupressAfterDuration.durationType)).valueOf();
      data.unsupressAfterDuration = unsupressAfterDuration;
      data.unsupress = true;
    }
    if (record.action === FINDING_ACTIONS.PROPOSE_DUE_DATE && actionTxt === 'Accept' && values.dueDate !== dueDateValue) {
      data.dueDate = values.dueDate;
    }
    const actionType = getActionType(record)?.key;
    const msg = `Processing ${actionTxt}  proposal, please check after some time.`;
    data.approved = actionTxt === 'Accept';
    data.findingIds = [record.id];
    const API = record.action === FINDING_ACTIONS.PROPOSE_CUSTOM_FIELDS ? FindingAPI.bulk.proposeAcceptRejectCustomFields : FindingAPI.bulk.proposeAcceptReject
    await API(data, actionType).promise.then(() => {
      message.success(msg);
    })
  }, [record, actionTxt, form, dueDateValue, customFieldPayload]);

  return (
    <BaseModalForm
      title={`Are you sure that you want to '${actionTxt}' the Proposal.`}
      form={form}
      fields={fields}
      ref={ref}
      afterClose={afterClose}
      onOk={onOk}
      modalProps={{ okText: actionTxt }}
      initialValues={{
        unsupressAfterDuration: UNSUPPRESS_DEFAULTS,
        dueDate: dueDateValue,
      }}
    />
  )
})

export const ProposalDetailComponent = ({proposedData, fetchFinding, ...restProps}) => {
  const getItems = useCallback((item, i) => {
    const userName = item.userEmail || "";
    const matches = userName.match(/\b(\w)/g);
    const actionType = getActionType(item);
    const params = { record: {...restProps, ...item}, action: 'ACCEPT', callBack: fetchFinding };
    return (
      <div key={i}>
        <Comment
          author={userName}
          avatar={<Avatar size="small">{matches?.join("") || userName[0]}</Avatar>}
          content={<Row>
            <Col span={20}>
            <div className="m-r-xs pull-left">{actionType.label} from</div> {getProposedRenderField({...restProps, armorcodeCategory: restProps.armorcodeCategory || restProps.category,  proposedData: [item]})}
            <strong className="m-r-xs">Note:</strong>{item.requestActionNote}
            </Col>
            <Col span={4} className="text-right">
              <ActionComponent {...{params, permission: actionType.permission}}/>
            </Col>
          </Row>}
          datetime={ (
            <Tooltip title={CellRenderers.TIME_RELATIVE(item.proposedOn)} placement="right">
                {CellRenderers.TIME(item.proposedOn)}
            </Tooltip>
        )}
        />
        {getDivider(i, proposedData)}
      </div>
    )
  }, [proposedData, fetchFinding, restProps])

  return _.map(proposedData, getItems)
}

export default FindingsReview

export function getCustomFieldsToText(jsonData) {
  return (
    <>
      {JSON.parse(jsonData).map(
        ({
          customFieldLabel,
          customFieldValue,
        }) => (
          <span key={customFieldLabel}>
            <b>{customFieldLabel}</b>: {customFieldValue}{" "}
          </span>
        ),
      )}
    </>
  );
}

export function getCustomFieldsFromValue(customFields, proposedValue) {
  if(isEmpty(customFields) ){
    if(!isEmpty(JSON.parse(proposedValue))){
      return  <>
      {JSON.parse(proposedValue).map(
        ({
          customFieldLabel,
        }) => (
          <span key={customFieldLabel}>
            <b>{customFieldLabel}</b>: {CellRenderers.VALUE_OR_NA()}&nbsp;
          </span>
        ),
      )}
    </>
    }
  }
  const parsedProposedValue = JSON.parse(proposedValue);
  const individualCustomField = filter(
    customFields,
    (field) => field.fieldId === parsedProposedValue[0]?.customFieldId,
  );

  const label = parsedProposedValue[0]?.customFieldLabel;
  const value = CellRenderers.VALUE_OR_NA(individualCustomField[0]?.fieldValue);

  return (
    <>
      <b>{label}</b>: {value}
    </>
  );
}